import { useContext } from 'react';
import { useLoaderData, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useImmerReducer } from 'use-immer';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import smartReducer from '../../../library/Core/SmartReducer';
import { DispatchEvent, State } from '../../../library/Core/SmartTypes';
import SmartContextPage from '../../../library/Core/SmartContextPage';
import AlumniCardGridControl from './AlumniCardGridControl';
import AlumniDirectoryService from './AlumniDirectoryService';
import DirectorySearchControl from './DirectorySearchControl';
import DirectorySummaryCardControl from './DirectorySummaryCardControl';
import SessionContext from '../../../library/Core/SessionContext';
import { seniorityCodesFromSearchParams } from '../../admin/profile-analytics-dashboard/directoryLinks';
import '../../../styles/css/revamp_style.css';

const AlumniDirectory = () => {
    const { pageName } = useParams();
    const { globalState } = useContext(GlobalContext);
    const { sessionState } = useContext(SessionContext);

    const [searchParams] = useSearchParams();
    const listParam = (key: string) =>
        searchParams
            .getAll(key)
            .flatMap((v) => String(v).split(','))
            .map((v) => v.trim())
            .filter(Boolean);
    const universityId = searchParams.get('universityId');
    const pgUniversityId = searchParams.get('pgUniversityId');
    const jobCompanyId = searchParams.get('jobCompanyId');
    const industryCode = searchParams.get('industryCode');
    const functionalAreaCode = searchParams.get('functionalAreaCode');
    const seniorityLevelCode = seniorityCodesFromSearchParams(searchParams);
    const instituteId = searchParams.get('instituteId');

    const navigate = useNavigate();

    // The route loader already fetched everything (including a preloaded search) before
    // this component ever mounted, so the initial state is built synchronously here rather
    // than starting with isDataLoading: true and patching it in a moment later via effect —
    // that gap used to cause a second loader flash right after the route-transition loader.
    const { formConfig, data, internal } = useLoaderData() as State;
    const domain = globalState.domain;
    const searchFilterDomains = AlumniDirectoryService.getSearchFilterDomains(domain, internal.searchFilterDomains);
    const filters = {
        ...AlumniDirectoryService.getDefaultSearchCriteria(data.searchBy),
        universityId: universityId ? [universityId] : [],
        pgUniversityId: pgUniversityId ? [pgUniversityId] : [],
        jobCompanyId: jobCompanyId ? [jobCompanyId] : [],
        industryCode: industryCode ? [industryCode] : [],
        functionalAreaCode: functionalAreaCode ? [functionalAreaCode] : [],
        seniorityLevelCode,
        instituteId: instituteId ? [instituteId] : [],
    };

    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(smartReducer, {
        data: { ...data, searchDomain: searchFilterDomains, filters, cardViewMode: 'CURRENT' },
        formConfig,
        internal: { ...internal, searchFilterDomains },
        domain,
        routeInfo: { pageName },
        flags: { isDataLoading: false, showFormErrors: 0 },
        formValidationErrors: {},
        actions: { ROUTER_NAVIGATE: navigate },
    } as any);

    const isPeerReviewed = sessionState?.isPeerReviewed ?? false;
    const userType = sessionState?.userTypeCode;
    const isPersonate = sessionStorage.getItem('is-impersonated-session') == 'YES' ? true : false;
    if (isPersonate != true && userType == 'ALUMNI' && isPeerReviewed != true) {
        window.location.href = `/alumni/welcome`;
        return;
    }

    if (
        sessionStorage.getItem('user-type-code') === 'STUDENT' &&
        sessionStorage.getItem('user-profile-status-code') === 'PREFERENCES_INCOMPLETE'
    ) {
        window.location.href = `/student/${sessionStorage.getItem('user-id')}/student-preference`;
        return null; // Redirection is initiated, so no need to render further
    }

    return (
        <SmartContextPage state={state} dispatch={dispatch} showDebug={false}>
            <>
                <DirectorySummaryCardControl />
                <DirectorySearchControl />
                <AlumniCardGridControl />
            </>
        </SmartContextPage>
    );
};

export default AlumniDirectory;
